import React from 'react';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ByzMuiSelect from 'src/components/core-components/mui/ByzMuiSelect';
import { ByzMuiDatePicker } from 'src/components/core-components/mui/inputs/ByzMuiDatePicker';
import ByzMuiTextField from 'src/components/core-components/mui/inputs/ByzMuiTextField';
import { ByzMuiCheckBox } from 'src/components/core-components/mui/ByzMuiCheckBox';
import ByzMuiSearchButton from 'src/components/core-components/mui/buttons/ByzMuiSearchButton';

interface OfficeOption {
  ID?: number | null;
  OFFICE_NAME?: string | null;
}

interface Props {
  t: (key: string) => string;
  userOffices?: OfficeOption[] | null;
  selectedOffice?: OfficeOption | null;
  startDate: Date | null;
  endDate: Date | null;
  searchType: 'dosya-tc' | 'protokol';
  searchValue: string;
  selectedStatus: number;
  statusOptions: Array<{ value: number; label: string }>;
  autoRefresh: boolean;
  showNoReport: boolean;
  showDischarged: boolean;
  showSurgeries: boolean;
  showProcessDetails: boolean;
  loading: boolean;
  onOfficeChange: (officeId: number) => void;
  onStartDateChange: (value: Date | null) => void;
  onEndDateChange: (value: Date | null) => void;
  onSearchTypeChange: (value: 'dosya-tc' | 'protokol') => void;
  onSearchValueChange: (value: string) => void;
  onSearch: () => void;
  onStatusChange: (value: number) => void;
  onAutoRefreshChange: (value: boolean) => void;
  onShowNoReportChange: (value: boolean) => void;
  onShowDischargedChange: (value: boolean) => void;
  onShowSurgeriesChange: (value: boolean) => void;
  onShowProcessDetailsChange: (value: boolean) => void;
}

export function OperatingRoomFilters({
  t,
  userOffices,
  selectedOffice,
  startDate,
  endDate,
  searchType,
  searchValue,
  selectedStatus,
  statusOptions,
  autoRefresh,
  showNoReport,
  showDischarged,
  showSurgeries,
  showProcessDetails,
  loading,
  onOfficeChange,
  onStartDateChange,
  onEndDateChange,
  onSearchTypeChange,
  onSearchValueChange,
  onSearch,
  onStatusChange,
  onAutoRefreshChange,
  onShowNoReportChange,
  onShowDischargedChange,
  onShowSurgeriesChange,
  onShowProcessDetailsChange,
}: Props) {
  return (
    <Paper sx={{ p: 1.5, mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={10}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4} md={4}>
                  <ByzMuiSelect
                    options={userOffices?.map((office) => ({
                      label: office.OFFICE_NAME || t('surgery.filters.officeNotSelected'),
                      value: String(office.ID || ''),
                    })) || []}
                    label={t('surgery.filters.office')}
                    inputLabel={t('surgery.filters.office')}
                    value={
                      selectedOffice?.ID
                        ? { label: selectedOffice.OFFICE_NAME || '', value: String(selectedOffice.ID) }
                        : null
                    }
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                        onOfficeChange(Number((newValue as { value: string }).value));
                      }
                    }}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ByzMuiDatePicker
                      label={t('surgery.filters.startDate')}
                      value={startDate}
                      onChange={onStartDateChange}
                      format="dd/MM/yyyy"
                      fullWidth
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ByzMuiDatePicker
                      minDate={startDate ? (() => {
                        const nextDay = new Date(startDate);
                        nextDay.setDate(nextDay.getDate() + 1);
                        return nextDay;
                      })() : undefined}
                      label={t('surgery.filters.endDate')}
                      value={endDate}
                      onChange={onEndDateChange}
                      format="dd/MM/yyyy"
                      fullWidth
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} sm={4} md={4}>
                  <ByzMuiSelect
                    options={[
                      { label: t('surgery.filters.fileTcNo'), value: 'dosya-tc' },
                      { label: t('surgery.filters.protocolNo'), value: 'protokol' },
                    ]}
                    label={t('surgery.filters.type')}
                    inputLabel={t('surgery.filters.type')}
                    value={
                      searchType === 'dosya-tc'
                        ? { label: t('surgery.filters.fileTcNo'), value: 'dosya-tc' }
                        : { label: t('surgery.filters.protocolNo'), value: 'protokol' }
                    }
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                        onSearchTypeChange((newValue as { value: 'dosya-tc' | 'protokol' }).value);
                      }
                    }}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <ByzMuiTextField
                    size="small"
                    label={searchType === 'dosya-tc' ? t('surgery.filters.fileTcNo') : t('surgery.filters.protocolNo')}
                    value={searchValue}
                    type="number"
                    inputProps={{ maxLength: 11, inputMode: 'numeric' }}
                    onChange={(e) => {
                      if (e.target.value.length <= 11) {
                        onSearchValueChange(e.target.value);
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') onSearch();
                    }}
                    sx={{ width: '100%' }}
                    placeholder={
                      searchType === 'dosya-tc'
                        ? t('surgery.filters.enterFileTcNo')
                        : t('surgery.filters.enterProtocolNo')
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <ByzMuiSelect
                    options={statusOptions.map((option) => ({
                      label: option.label,
                      value: option.value.toString(),
                    }))}
                    label={t('surgery.filters.status')}
                    inputLabel={t('surgery.filters.status')}
                    value={
                      statusOptions.find((option) => option.value === selectedStatus)
                        ? {
                            label: statusOptions.find((option) => option.value === selectedStatus)!.label,
                            value: selectedStatus.toString(),
                          }
                        : null
                    }
                    onChange={(_, newValue) => {
                      if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                        onStatusChange(Number((newValue as { value: string }).value));
                      }
                    }}
                    size="small"
                    sx={{ width: '100%' }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    <ByzMuiCheckBox
                      checked={autoRefresh}
                      onChange={(e) => onAutoRefreshChange(e.target.checked)}
                      size="small"
                      text={t('surgery.filters.autoRefresh')}
                      sx={{ fontSize: '0.8rem' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={2} sx={{ alignSelf: 'flex-start', mt: -3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: { xs: 'auto', md: 'calc((36px + 20px + 12px) * 2 - 12px)' },
                  minHeight: { xs: 'auto', md: '124px' },
                }}
              >
                <ByzMuiSearchButton onClick={onSearch} disabled={loading} />
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              borderLeft: { md: '1px solid #e0e0e0' },
              pl: { md: 2 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button disabled variant="outlined" size="small" sx={{ minWidth: '120px' }}>
                {t('surgery.filters.editSurgeries')}
              </Button>
              <Button disabled variant="outlined" size="small" sx={{ minWidth: '100px' }}>
                {t('surgery.filters.saveAs')}
              </Button>
              <Button disabled variant="outlined" size="small" sx={{ minWidth: '80px' }}>
                {t('surgery.filters.print')}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <ByzMuiCheckBox disabled size="small" text={t('surgery.filters.color')} sx={{ fontSize: '0.8rem' }} />
              <Button disabled variant="outlined" size="small" sx={{ minWidth: '100px' }}>
                {t('surgery.filters.requestFile')}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 2, color: '#666' }}>
            {t('surgery.filters.toShow')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <ByzMuiCheckBox
            checked={showNoReport}
            onChange={(e) => onShowNoReportChange(e.target.checked)}
            size="small"
            text={t('surgery.filters.showWithoutReport')}
            sx={{ fontSize: '0.8rem' }}
          />
          <ByzMuiCheckBox
            checked={showDischarged}
            onChange={(e) => onShowDischargedChange(e.target.checked)}
            size="small"
            text={t('surgery.filters.showDischarged')}
            sx={{ fontSize: '0.8rem' }}
          />
          <ByzMuiCheckBox
            checked={showSurgeries}
            onChange={(e) => onShowSurgeriesChange(e.target.checked)}
            size="small"
            text={t('surgery.filters.showSurgeryList')}
            sx={{ fontSize: '0.8rem' }}
          />
          <ByzMuiCheckBox
            checked={showProcessDetails}
            onChange={(e) => onShowProcessDetailsChange(e.target.checked)}
            size="small"
            text={t('surgery.filters.showProcessDetails')}
            sx={{ fontSize: '0.8rem' }}
          />
        </Stack>
      </Box>
    </Paper>
  );
}
